import fs from 'node:fs';
import { isMainThread } from 'node:worker_threads';
import type { LogFn } from '../../log.js';
import { logImmediate } from '../../log.js';
import { isCliSqliteLockError } from '../sqlite-errors.js';
import { getSqliteLockPath } from '../sqlite-lock-path.js';
import { getCodeNavCacheSqlitePath } from './code-nav-cache-paths.js';

type CodeNavCacheDiagnosticSink = (line: string) => void;

let diagnosticSink: CodeNavCacheDiagnosticSink = logImmediate;
let lastDiagnosticKey: string | null = null;

export function setCodeNavCacheDiagnosticSink(sink: CodeNavCacheDiagnosticSink): void {
  diagnosticSink = sink;
}

export function resetCodeNavCacheLogForTests(): void {
  lastDiagnosticKey = null;
  diagnosticSink = logImmediate;
}

function codeNavCacheLockPath(): string {
  return getSqliteLockPath(getCodeNavCacheSqlitePath());
}

function lockDirectoryPresent(): boolean {
  try {
    return fs.existsSync(codeNavCacheLockPath());
  } catch {
    return false;
  }
}

function emitCodeNavCacheDiagnostic(key: string, line: string): void {
  if (lastDiagnosticKey === key) return;
  lastDiagnosticKey = key;
  diagnosticSink(line);
}

function formatLockBlockedLine(detail: string): string {
  const lockPath = codeNavCacheLockPath();
  const dbPath = getCodeNavCacheSqlitePath();
  if (lockDirectoryPresent()) {
    return `[code-nav] Code navigation cache blocked (${detail}). Remove stale lock when no other buildautomaton CLI is running: ${lockPath}`;
  }
  return `[code-nav] Code navigation cache blocked (${detail}). Database: ${dbPath} Lock: ${lockPath}`;
}

/** Log once at bridge startup when a stale lock directory is already present. */
export function logCodeNavCacheLockPresentAtStartup(log: LogFn = logImmediate): void {
  if (!lockDirectoryPresent()) return;
  const lockPath = codeNavCacheLockPath();
  log(
    `[code-nav] Code navigation cache lock directory exists at startup (${lockPath}). Indexing will retry briefly; remove this directory if no other buildautomaton CLI is running.`,
  );
}

/** Log SQLite lock/contention for the code-nav cache (main thread or worker via diagnostic sink). */
export function reportCodeNavCacheLockContention(
  detail: string,
  opts?: { attempt?: number; maxAttempts?: number; final?: boolean },
): void {
  const attemptSuffix =
    opts?.attempt != null && opts?.maxAttempts != null
      ? ` [attempt ${opts.attempt}/${opts.maxAttempts}${opts.final ? ', giving up' : ''}]`
      : opts?.final
        ? ' [giving up]'
        : '';
  emitCodeNavCacheDiagnostic(
    `lock:${detail}:${opts?.attempt ?? 0}:${opts?.final ?? false}`,
    `${formatLockBlockedLine(detail)}${attemptSuffix}`,
  );
}

export function logCodeNavCacheUnavailableFromError(log: LogFn, err: unknown): void {
  const detail = err instanceof Error ? err.message : String(err);
  log(`${formatLockBlockedLine(detail)}`);
  lastDiagnosticKey = `unavailable:${detail}`;
}

export function maybeLogCodeNavCacheWorkerError(message: string): void {
  const err = new Error(message);
  if (
    isCliSqliteLockError(err) ||
    message.toLowerCase().includes('code navigation cache') ||
    message.toLowerCase().includes('unavailable')
  ) {
    reportCodeNavCacheLockContention(`symbol index worker: ${message}`, { final: true });
  }
}

/** True when worker-thread diagnostics should post to the main CLI log. */
export function isCodeNavCacheDiagnosticWorkerThread(): boolean {
  return !isMainThread;
}
